import { getAssessmentScoreColor } from "@repo/app-types/constants/assessmentScore";
import {
  getPassScoreColor,
  getPassScoreLabel,
} from "@repo/app-types/constants/passScore";
import {
  formatCoachingScore,
  type CoachingGradingValueType,
} from "@repo/app-types/entities/coachingScore";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { IconExternalLink } from "@tabler/icons-react";
import type { CSSProperties, FC } from "react";
import { Size } from "../helpers/helpers.js";
import { getPillColor, Pill, type PillColor } from "../Pills/pill.js";
import type { CoachingGoalCallPoint } from "./coachingGoalTypes.js";

export interface CoachingGoalCallPopoverProps {
  call: CoachingGoalCallPoint;
  gradingValueType: CoachingGradingValueType;
  onCallOpen?: (gradingId: GradingId) => void;
}

/**
 * The call card shown by the trend meter. It is the whole external call link;
 * positioning, the pointer arrow, and the hover-keep handlers are supplied by
 * the react-aria `Popover` that wraps it in the meter — this component only
 * renders the card.
 */
export const CoachingGoalCallPopover: FC<CoachingGoalCallPopoverProps> = (
  props,
) => {
  const { call, gradingValueType, onCallOpen } = props;
  const scorePill = buildCallScorePill(call.score, gradingValueType);

  return (
    <a
      href={call.href}
      // Just the interactive content; the surface (rounded corners, bg, ring,
      // shadow) comes from the shared popover style on the wrapping <Popover>.
      // Matching `rounded-md` keeps the hover fill inside the wrapper's corners.
      className="group block w-[var(--call-popover-width)] cursor-pointer rounded-md text-left no-underline transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
      rel="noopener noreferrer"
      style={
        {
          "--call-popover-customer-max-width": CALL_POPOVER_CUSTOMER_MAX_WIDTH,
          "--call-popover-email-max-width": CALL_POPOVER_EMAIL_MAX_WIDTH,
          "--call-popover-width": CALL_POPOVER_WIDTH,
        } as CSSProperties
      }
      target="_blank"
      onClick={() => onCallOpen?.(call.id)}
    >
      <div className="flex items-center justify-between gap-3 p-3 text-sm">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="max-w-[var(--call-popover-customer-max-width)] min-w-0 truncate leading-5 font-medium text-gray-900">
              {call.customer}
            </p>
            <p className="flex shrink-0 items-center gap-1 text-xs leading-4 whitespace-nowrap text-gray-600">
              <span>{call.recordedAtLabel}</span>
              <span className="text-gray-400">•</span>
              <span>{call.callSummaryLabel}</span>
            </p>
          </div>
          <p className="max-w-[var(--call-popover-email-max-width)] min-w-0 truncate leading-5 text-gray-600">
            {call.email}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <Pill
            color={scorePill.color}
            label={<span className="tabular-nums">{scorePill.label}</span>}
            size={Size.SMALL}
          />
          <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-gray-900">
            <IconExternalLink className="size-5" />
          </span>
        </div>
      </div>
    </a>
  );
};

/**
 * The score pill for a single call in the popover. A call is one grading, so its
 * score is rendered on the individual-call scale — never the aggregate one:
 * yes/no shows a Yes/No pass pill (not the 0% / 100% pass-rate the running
 * average uses), and a 1-5 score keeps its numeric label but takes the graduated
 * assessment-score color shared with the playbook table, instead of a flat pill.
 */
function buildCallScorePill(
  score: number,
  gradingValueType: CoachingGradingValueType,
): { color: PillColor; label: string } {
  if (gradingValueType === "yes_no") {
    const passed = score >= 0.5;
    return {
      color: getPillColor(getPassScoreColor(passed)),
      label: getPassScoreLabel(passed),
    };
  }

  return {
    color: getPillColor(getAssessmentScoreColor(score)),
    label: formatCoachingScore(score, gradingValueType),
  };
}

const CALL_POPOVER_CUSTOMER_MAX_WIDTH = "8rem";
const CALL_POPOVER_EMAIL_MAX_WIDTH = "12.5rem";
const CALL_POPOVER_WIDTH = "20rem";
